import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { ServiceHistoryRecord } from '../../shared/types';
import { DISCOUNT_TYPE_BADGE_LABELS } from '../../discount/constants/discount.constants';
import { formatDiscountAmount } from '../../discount/utils/discount-display.util';

/** One applied discount usage for the Customer Details "Discounts Used" tab. */
export interface CustomerDiscountUsedRow {
  orderUuid: string;
  orderId: string;
  displayOrderId: string;
  discountName: string;
  discountTypeLabel: string;
  discountAmountDisplay: string;
  appliedDate: string;
}

/**
 * Lists discounts that were actually applied on this customer's orders.
 * Reuses GET customer/:id/service-history (order → discount join) and filters to applied rows.
 */
@Component({
  selector: 'app-discounts-used',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './discounts-used.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./discounts-used.component.scss'],
})
export class DiscountsUsedComponent implements OnChanges {
  @Input() customerId = '';

  rows: CustomerDiscountUsedRow[] = [];
  loading = false;
  error: string | null = null;

  constructor(
    private apiService: ApiService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId'] && this.customerId) {
      this.loadDiscountsUsed();
    }
  }

  loadDiscountsUsed(): void {
    if (!this.customerId) {
      this.error = 'Customer ID not found';
      this.rows = [];
      return;
    }

    this.loading = true;
    this.error = null;

    this.apiService.getServiceHistory(this.customerId).subscribe({
      next: response => {
        if (response.status && response.data) {
          this.rows = this.mapAppliedDiscountRows(response.data);
        } else {
          this.rows = [];
          this.error = response.message || 'Failed to load discounts used';
        }
        this.loading = false;
      },
      error: err => {
        console.error('Error loading discounts used:', err);
        this.rows = [];
        this.error = 'Failed to load discounts used. Please try again.';
        this.loading = false;
      },
    });
  }

  onViewOrder(row: CustomerDiscountUsedRow): void {
    void this.router.navigate(['/orders'], { queryParams: { orderId: row.orderUuid } });
  }

  /**
   * Only orders with an applied catalog/custom discount (`orders.discount_id`).
   * Falls back to joined discount fields when older payloads omit discount_id.
   */
  private mapAppliedDiscountRows(records: ServiceHistoryRecord[]): CustomerDiscountUsedRow[] {
    return records
      .filter(record => this.hasAppliedDiscount(record))
      .map(record => {
        const orderId = String(record.order_id || record.id || '')
          .trim()
          .replace(/^#+/, '');
        const displayOrderId = orderId ? `#${orderId}` : '-';
        const discountType = String(record.discount_type || '').trim();
        const appliedDate =
          record.payment_date || record.appointment_time || record.created_at || '';

        return {
          orderUuid: record.id,
          orderId,
          displayOrderId,
          discountName: String(record.discount_name || '').trim() || '-',
          discountTypeLabel: DISCOUNT_TYPE_BADGE_LABELS[discountType] || discountType || '-',
          discountAmountDisplay: this.resolveAmountDisplay(record),
          appliedDate,
        };
      });
  }

  private hasAppliedDiscount(record: ServiceHistoryRecord): boolean {
    const discountId = String(record.discount_id ?? '').trim();
    if (discountId) {
      return true;
    }
    return Boolean(
      String(record.discount_name ?? '').trim() || String(record.discount_type ?? '').trim()
    );
  }

  private resolveAmountDisplay(record: ServiceHistoryRecord): string {
    const fromDefinition = formatDiscountAmount(record.discount_type, record.discount_value);
    if (fromDefinition !== '-') {
      return fromDefinition;
    }
    const appliedAmount = Number(record.discount_amount);
    if (Number.isFinite(appliedAmount) && appliedAmount > 0) {
      return `$${String(Number(appliedAmount.toFixed(2)))}`;
    }
    return '-';
  }
}
